import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable';
import { useAuth } from '../../context/AuthContext';
import { useSectionColumnPreferences } from '../../context/SectionColumnPreferencesContext';
import RenameSectionColumnsModal from '../../components/RenameSectionColumnsModal';
import BulkSectionConfigModal from '../../components/BulkSectionConfigModal';
import SearchFilterPanel from '../../components/SearchFilterPanel';
const SectionConfigurationsPage = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { preferences } = useSectionColumnPreferences();
    const [configs, setConfigs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState({});
    // Reference data for dropdowns
    const [classifications, setClassifications] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [productionLines, setProductionLines] = useState([]);
    const [doorModels, setDoorModels] = useState([]);
    const [sectionTypes, setSectionTypes] = useState([]);
    const [sectionWidths, setSectionWidths] = useState([]);
    const [sectionHeights, setSectionHeights] = useState([]);
    const fetchReferenceData = async () => {
        try {
            const [class_res, mat_res, prod_res, mod_res, stype_res, swidth_res, sheight_res] = await Promise.all([
                fetch('/api/pricing/classifications', { credentials: 'include' }),
                fetch('/api/pricing/material_types', { credentials: 'include' }),
                fetch('/api/pricing/production_lines', { credentials: 'include' }),
                fetch('/api/pricing/door_models', { credentials: 'include' }),
                fetch('/api/pricing/section_types', { credentials: 'include' }),
                fetch('/api/pricing/section_widths', { credentials: 'include' }),
                fetch('/api/pricing/section_heights', { credentials: 'include' }),
            ]);
            const [class_data, mat_data, prod_data, mod_data, stype_data, swidth_data, sheight_data] = await Promise.all([
                class_res.json(),
                mat_res.json(),
                prod_res.json(),
                mod_res.json(),
                stype_res.json(),
                swidth_res.json(),
                sheight_res.json(),
            ]);
            setClassifications(class_data.data || []);
            setMaterialTypes(mat_data.data || []);
            setProductionLines(prod_data.data || []);
            setDoorModels(mod_data.data || []);
            setSectionTypes(stype_data.data || []);
            setSectionWidths(swidth_data.data || []);
            setSectionHeights(sheight_data.data || []);
        }
        catch (err) {
            console.error('Failed to load reference data:', err);
        }
    };
    const fetchConfigs = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await fetch('/api/section-configs', {
                credentials: 'include',
            });
            if (response.status === 401) {
                navigate('/login');
                return;
            }
            const result = await response.json();
            setConfigs(result.data || []);
        }
        catch (err) {
            setError(err.message || 'Failed to load section configurations');
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetchReferenceData();
        fetchConfigs();
    }, []);
    // Build filter configuration from reference data
    const filterConfigs = useMemo(() => [
        {
            key: 'classification_id',
            label: 'Classification',
            type: 'multi-select',
            options: classifications.map(c => ({ value: c.id, label: c.name })),
        },
        {
            key: 'material_type_id',
            label: 'Construction',
            type: 'multi-select',
            options: materialTypes.map(m => ({ value: m.id, label: m.name })),
        },
        {
            key: 'production_line_id',
            label: 'Production Line',
            type: 'multi-select',
            options: productionLines.map(p => ({ value: p.id, label: p.name })),
        },
        {
            key: 'door_model_id',
            label: 'Series',
            type: 'multi-select',
            options: doorModels.map(m => ({
                value: m.id,
                label: m.model_group_name ? `${m.model_group_name} - ${m.name}` : m.name
            })),
        },
        {
            key: 'section_type_id',
            label: 'Section Type',
            type: 'multi-select',
            options: sectionTypes.map(t => ({ value: t.id, label: t.name })),
        },
        {
            key: 'section_width_id',
            label: 'Section Width',
            type: 'multi-select',
            options: sectionWidths.map(w => ({ value: w.id, label: w.display_name || w.name })),
        },
        {
            key: 'section_height_id',
            label: 'Section Height',
            type: 'multi-select',
            options: sectionHeights.map(h => ({ value: h.id, label: h.display_name || h.name })),
        },
        {
            key: 'cogs',
            label: 'Material Cost ($)',
            type: 'range',
            options: [],
        },
        {
            key: 'list_price_calculated',
            label: 'List Price ($)',
            type: 'range',
            options: [],
        },
    ], [classifications, materialTypes, productionLines, doorModels, sectionTypes, sectionWidths, sectionHeights]);
    // Filter configs based on search query and active filters
    const filteredConfigs = useMemo(() => {
        let result = [...configs];
        // Apply text search across multiple fields
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(config => {
                const searchableFields = [
                    config.classification_name,
                    config.material_type_name,
                    config.production_line_name,
                    config.door_model_group_name,
                    config.door_model_name,
                    config.section_type_name,
                    config.section_width_name,
                    config.section_height_name,
                    config.cogs?.toString(),
                    config.total_cogs?.toString(),
                    config.list_price_calculated?.toString(),
                ];
                return searchableFields.some(field => field && field.toLowerCase().includes(query));
            });
        }
        // Apply active filters
        Object.entries(activeFilters).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
                // Multi-select filter
                result = result.filter(config => value.includes(config[key]));
            }
            else if (typeof value === 'object' && value !== null) {
                // Range filter
                const range = value;
                result = result.filter(config => {
                    const configValue = parseFloat(config[key]);
                    if (isNaN(configValue))
                        return false;
                    if (range.min !== undefined && configValue < range.min)
                        return false;
                    if (range.max !== undefined && configValue > range.max)
                        return false;
                    return true;
                });
            }
        });
        return result;
    }, [configs, searchQuery, activeFilters]);
    // Memoized callbacks for search panel
    const handleSearchChange = useCallback((query) => {
        setSearchQuery(query);
    }, []);
    const handleFiltersChange = useCallback((filters) => {
        setActiveFilters(filters);
    }, []);
    const handleAdd = async (newRow) => {
        try {
            // Ensure all numeric fields have values (0 as default if not set)
            const completeRow = {
                ...newRow,
                cogs: newRow.cogs ?? 0,
                cushion_v1: newRow.cushion_v1 ?? 0,
                cushion_v2: newRow.cushion_v2 ?? 0,
                cushion_v3: newRow.cushion_v3 ?? 0,
                cushion_v4: newRow.cushion_v4 ?? 0,
                divisor_d1: newRow.divisor_d1 ?? 0.01,
            };
            console.log('Sending section config data to API:', completeRow);
            const response = await fetch('/api/section-configs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(completeRow),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to add section configuration');
            }
            await fetchConfigs();
        }
        catch (err) {
            alert(err.message);
            throw err;
        }
    };
    const handleUpdate = async (id, updatedRow) => {
        try {
            const response = await fetch(`/api/section-configs/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(updatedRow),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to update section configuration');
            }
            await fetchConfigs();
        }
        catch (err) {
            alert(err.message);
            throw err;
        }
    };
    const handleDelete = async (id) => {
        try {
            const response = await fetch(`/api/section-configs/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to delete section configuration');
            }
            await fetchConfigs();
        }
        catch (err) {
            alert(err.message);
            throw err;
        }
    };
    const handleBulkGenerate = async (generatedConfigs) => {
        try {
            const response = await fetch('/api/section-configs/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(generatedConfigs),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to bulk add section configurations');
            }
            const result = await response.json();
            console.log('Bulk add result:', result);
            await fetchConfigs();
        }
        catch (err) {
            console.error('Bulk add error:', err);
            throw err;
        }
    };
    const columns = [
        {
            key: 'classification_id',
            label: 'Classification',
            type: 'select',
            required: true,
            width: 'w-40',
            options: classifications.map((c) => ({ value: c.id, label: c.name })),
            render: (value, row) => row.classification_name || value,
        },
        {
            key: 'material_type_id',
            label: 'Construction',
            type: 'select',
            required: true,
            width: 'w-40',
            options: materialTypes.map((m) => ({ value: m.id, label: m.name })),
            render: (value, row) => row.material_type_name || value,
        },
        {
            key: 'production_line_id',
            label: 'Production Line',
            type: 'select',
            required: true,
            width: 'w-40',
            options: productionLines.map((p) => ({ value: p.id, label: p.name })),
            render: (value, row) => row.production_line_name || value,
        },
        {
            key: 'door_model_id',
            label: 'Series',
            type: 'select',
            required: true,
            width: 'w-36',
            options: doorModels.map((m) => ({
                value: m.id,
                label: m.model_group_name ? `${m.model_group_name} - ${m.name}` : m.name
            })),
            render: (value, row) => {
                // Show "Door Model - Series" format
                const model = doorModels.find(m => m.id === value);
                if (model && model.model_group_name) {
                    return `${model.model_group_name} - ${model.name}`;
                }
                return row.door_model_name || value;
            },
        },
        {
            key: 'door_model_group_id',
            label: 'Door Model',
            type: 'calculated',
            width: 'w-32',
            render: (value, row) => row.door_model_group_name || '-',
        },
        {
            key: 'section_type_id',
            label: 'Section Type',
            type: 'select',
            required: true,
            width: 'w-40',
            options: sectionTypes.map((t) => ({ value: t.id, label: t.name })),
            render: (value, row) => row.section_type_name || value,
        },
        {
            key: 'section_width_id',
            label: 'Section Width',
            type: 'select',
            required: true,
            width: 'w-36',
            options: sectionWidths.map((w) => ({ value: w.id, label: w.display_name })),
            render: (value, row) => row.section_width_name || value,
        },
        {
            key: 'section_height_id',
            label: 'Section Height',
            type: 'select',
            required: true,
            width: 'w-36',
            options: sectionHeights.map((h) => ({ value: h.id, label: h.display_name })),
            render: (value, row) => row.section_height_name || value,
        },
        {
            key: 'cogs',
            label: 'Material Cost ($)',
            type: 'number',
            required: true,
            width: 'w-28',
            min: 0,
            step: 0.01,
            render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
        },
        {
            key: 'cushion_v1',
            label: preferences.cushion_v1,
            type: 'number',
            required: true,
            width: 'w-24',
            min: 0,
            max: 1,
            step: 0.01,
            defaultValue: 0,
        },
        {
            key: 'cushion_v2',
            label: preferences.cushion_v2,
            type: 'number',
            required: true,
            width: 'w-24',
            min: 0,
            max: 1,
            step: 0.01,
            defaultValue: 0,
        },
        {
            key: 'cushion_v3',
            label: preferences.cushion_v3,
            type: 'number',
            required: true,
            width: 'w-24',
            min: 0,
            max: 1,
            step: 0.01,
            defaultValue: 0,
        },
        {
            key: 'cushion_v4',
            label: preferences.cushion_v4,
            type: 'number',
            required: true,
            width: 'w-24',
            min: 0,
            max: 1,
            step: 0.01,
            defaultValue: 0,
        },
        {
            key: 'total_cogs',
            label: 'Total Material Cost',
            type: 'calculated',
            width: 'w-32',
            render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
        },
        {
            key: 'divisor_d1',
            label: preferences.divisor_d1,
            type: 'number',
            required: true,
            width: 'w-24',
            min: 0.01,
            max: 1,
            step: 0.01,
            defaultValue: 0.01,
        },
        {
            key: 'list_price_calculated',
            label: 'List Price',
            type: 'calculated',
            width: 'w-32',
            render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
        },
    ];
    return (_jsxs("div", { className: "min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50", children: [_jsx("nav", { className: "bg-white shadow-sm border-b border-secondary-200", children: _jsx("div", { className: "container-custom py-4", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center space-x-4", children: [_jsx("button", { onClick: () => navigate('/dashboard'), className: "text-secondary-600 hover:text-secondary-900", children: "\u2190 Back to Dashboard" }), _jsx("span", { className: "text-secondary-300", children: "|" }), _jsx("h1", { className: "text-xl font-bold text-secondary-900", children: "Sections Only Cost Configurations" })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("button", { onClick: () => navigate('/pricing/view-section-cost'), className: "text-sm text-indigo-600 hover:text-indigo-700 font-medium", children: "View Cost \u2192" }), _jsx("button", { onClick: logout, className: "btn btn-outline text-sm py-2", children: "Logout" })] })] }) }) }), _jsxs("main", { className: "container-custom py-8", children: [_jsx("div", { className: "mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg", children: _jsxs("div", { className: "flex items-start justify-between", children: [_jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: "font-semibold text-primary-900 mb-2", children: "Formula:" }), _jsx("p", { className: "text-sm text-primary-800", children: _jsx("code", { className: "bg-white px-2 py-1 rounded", children: "List Price = (MC + MC\u00D7V1 + MC\u00D7V2 + MC\u00D7V3 + MC\u00D7V4) / D1" }) }), _jsx("p", { className: "text-xs text-primary-700 mt-2", children: "Where MC = Material Cost, V1/V2/V3/V4 = Cushion variables (0-1), D1 = Divisor (0-1)" })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("button", { onClick: () => setShowBulkModal(true), className: "flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm whitespace-nowrap", children: [_jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 6v6m0 0v6m0-6h6m-6 0H6" }) }), "Bulk Add"] }), _jsxs("button", { onClick: () => setShowRenameModal(true), className: "flex items-center gap-2 px-4 py-2 bg-white border-2 border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors font-medium text-sm whitespace-nowrap", children: [_jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }) }), "Rename Columns"] })] })] }) }), _jsx(SearchFilterPanel, { filters: filterConfigs, activeFilters: activeFilters, onFiltersChange: handleFiltersChange, searchQuery: searchQuery, onSearchChange: handleSearchChange, totalCount: configs.length, filteredCount: filteredConfigs.length, searchPlaceholder: "Search by classification, construction, series, section type, dimensions, cost..." }), _jsx("div", { className: "mb-4 flex items-center justify-between", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm text-secondary-600", children: "Total Cost Configurations:" }), _jsx("span", { className: "px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold", children: configs.length.toLocaleString() }), filteredConfigs.length !== configs.length && (_jsxs("span", { className: "text-sm text-secondary-500", children: ["(showing ", filteredConfigs.length.toLocaleString(), " filtered)"] }))] }) }), _jsxs("div", { className: "card", children: [error && (_jsx("div", { className: "mb-4 p-3 bg-error-50 border border-error-200 text-error-700 rounded-lg text-sm", children: error })), _jsx(DataTable, { columns: columns, data: filteredConfigs, onAdd: handleAdd, onUpdate: handleUpdate, onDelete: handleDelete, isLoading: isLoading, emptyMessage: configs.length === 0
                                    ? "No section configurations found. Click 'Add New' to create one."
                                    : "No configurations match your search criteria. Try adjusting your filters." })] })] }), _jsx(RenameSectionColumnsModal, { isOpen: showRenameModal, onClose: () => setShowRenameModal(false) }), _jsx(BulkSectionConfigModal, { isOpen: showBulkModal, onClose: () => setShowBulkModal(false), onGenerate: handleBulkGenerate, existingConfigs: configs })] }));
};
export default SectionConfigurationsPage;
